/**
 * PRESS — trigger the browser's native print/PDF flow for a StudyDoc.
 *
 * Print CSS (studyDocPrint.css) targets `[data-study-doc-print-root]` via a
 * `:has()` media-print rule, so no JS-side class toggling is needed to arm
 * it — native ⌘P works exactly like this explicit button. Plot embeds
 * rasterize themselves on mount (`StudyDocResolvedPlotEmbed` in
 * StudyDocBody.tsx) and keep a print-only `<img>` fallback in the DOM at
 * all times, so `window.print()` alone is enough here too.
 *
 * The browser names the saved PDF from `document.title`, and paints the
 * same title as a Times header when "Headers and footers" is ticked. So
 * while printing, the title is the report's own name (read from the print
 * root's `data-study-doc-print-title`, button and ⌘P alike). Where no single
 * report is being printed the title is blanked, as before, and the running
 * header in the page uses a system sans stack. The page title is restored
 * after printing.
 */

import i18n from '@/i18n'

import { buildPrintDocumentTitle } from './printDocumentTitle'
import './studyDocPrint.css'

export interface PrintStudyDocOptions {
  title?: string
}

const PRINT_TITLE_BLANK = '\u00A0'
const TITLE_RESTORE_ATTR = 'data-study-doc-print-title-restore'

const PRINT_ROOT_SELECTOR = '[data-study-doc-print-root]'
const PRINT_TITLE_ATTR = 'data-study-doc-print-title'

/**
 * The root the print CSS lets through: first inside full screen, else the
 * first inside the rail, else the only root in the document. Two or more
 * inline roots all print, so no single name is true and there is no winner.
 */
function findWinningPrintRoot(): Element | null {
  const fullscreenRoot = document.querySelector(
    `[data-testid='thread-artifact-fullscreen'] ${PRINT_ROOT_SELECTOR}`,
  )
  if (fullscreenRoot) {
    return fullscreenRoot
  }
  const expandedRoot = document.querySelector(
    `[data-testid='thread-artifact-expanded'] ${PRINT_ROOT_SELECTOR}`,
  )
  if (expandedRoot) {
    return expandedRoot
  }
  const roots = document.querySelectorAll(PRINT_ROOT_SELECTOR)
  return roots.length === 1 ? roots[0] : null
}

function resolvePrintTitle(explicitTitle?: string): string {
  const fallback = i18n.t('library.type.report', { ns: 'app' })
  if (explicitTitle != null && explicitTitle.trim() !== '') {
    return buildPrintDocumentTitle(explicitTitle, fallback)
  }
  const root = findWinningPrintRoot()
  const named = root?.closest(`[${PRINT_TITLE_ATTR}]`)
  if (!named) {
    return PRINT_TITLE_BLANK
  }
  return buildPrintDocumentTitle(named.getAttribute(PRINT_TITLE_ATTR), fallback)
}

function setDocumentTitleForPrint(explicitTitle?: string) {
  if (document.documentElement.hasAttribute(TITLE_RESTORE_ATTR)) {
    return
  }
  document.documentElement.setAttribute(TITLE_RESTORE_ATTR, document.title)
  document.title = resolvePrintTitle(explicitTitle)
}

function handleBeforePrint() {
  setDocumentTitleForPrint()
}

function restoreDocumentTitle() {
  const previous = document.documentElement.getAttribute(TITLE_RESTORE_ATTR)
  if (previous == null) {
    return
  }
  document.title = previous
  document.documentElement.removeAttribute(TITLE_RESTORE_ATTR)
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeprint', handleBeforePrint)
  window.addEventListener('afterprint', restoreDocumentTitle)
}

export async function printStudyDoc(
  options: PrintStudyDocOptions = {},
): Promise<void> {
  setDocumentTitleForPrint(options.title)
  window.print()
}
